import { useEffect, useState, useMemo } from "react";
import Layout from "../components/Layout";
import { useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { toast } from "sonner";
import { 
  Plus, 
  Trash2, 
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Bell,
  Sparkles,
  DollarSign,
  AlertCircle,
  Clock,
  Repeat,
  Mail,
  Settings,
  Send,
  CheckCircle
} from "lucide-react";

const formatCurrency = (value) => {
  if (!value) return "-";
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
};

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];

const EVENT_COLORS = {
  payment: "bg-red-500",
  income: "bg-primary",
  reminder: "bg-amber-500",
  other: "bg-violet-500"
};

export default function Calendar() {
  const { api } = useAuth();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [emailSending, setEmailSending] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [upcoming, setUpcoming] = useState({ upcoming_events: [], reminders: [] });
  
  const [notificationSettings, setNotificationSettings] = useState({
    email_enabled: false,
    email_address: "",
    reminder_days_before: 3,
    daily_digest: true,
    digest_time: "08:00"
  });
  
  const [formData, setFormData] = useState({
    title: "",
    event_type: "payment",
    date: new Date().toISOString().split('T')[0],
    amount: "",
    category: "",
    is_recurring: false,
    recurring_frequency: "monthly",
    notes: "",
    reminder_days: 3
  });

  useEffect(() => {
    fetchEvents();
    fetchUpcoming();
    fetchNotificationSettings();
  }, [currentMonth, currentYear]);

  const fetchNotificationSettings = async () => {
    try {
      const res = await api.get("/notifications/settings");
      setNotificationSettings(res.data);
    } catch (error) {
      console.error("Failed to fetch notification settings");
    }
  };

  const fetchEvents = async () => {
    try {
      const res = await api.get(`/calendar?month=${currentMonth + 1}&year=${currentYear}`);
      setEvents(res.data);
    } catch (error) {
      toast.error("Failed to fetch calendar events");
    } finally {
      setLoading(false);
    }
  };

  const fetchUpcoming = async () => {
    try {
      const res = await api.get("/calendar/upcoming?days=14");
      setUpcoming(res.data);
    } catch (error) {
      console.error("Failed to fetch upcoming events");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/calendar", {
        ...formData,
        amount: formData.amount ? parseFloat(formData.amount) : null,
        reminder_days: parseInt(formData.reminder_days)
      });
      toast.success("Event added to calendar");
      setDialogOpen(false);
      resetForm();
      fetchEvents();
      fetchUpcoming();
    } catch (error) {
      toast.error("Failed to add event");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this event?")) return;
    try {
      await api.delete(`/calendar/${id}`);
      toast.success("Event deleted");
      fetchEvents();
      fetchUpcoming();
    } catch (error) {
      toast.error("Failed to delete event");
    }
  };

  const handleAiSuggest = async () => {
    setAiLoading(true);
    try {
      const res = await api.post("/calendar/ai-suggest");
      toast.success(res.data.message);
      fetchEvents();
      fetchUpcoming();
    } catch (error) {
      toast.error("AI suggestion failed");
    } finally {
      setAiLoading(false);
    }
  };

  const handleSaveNotificationSettings = async () => {
    try {
      await api.put("/notifications/settings", notificationSettings);
      toast.success("Notification settings saved");
      setSettingsDialogOpen(false);
    } catch (error) {
      toast.error("Failed to save settings");
    }
  };

  const handleSendReminders = async () => {
    if (!notificationSettings.email_enabled || !notificationSettings.email_address) {
      toast.error("Please enable email notifications and add your email address first");
      setSettingsDialogOpen(true);
      return;
    }
    
    setEmailSending(true);
    try {
      const res = await api.post("/notifications/send-reminders");
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send reminders");
    } finally {
      setEmailSending(false);
    }
  };

  const handleTestEmail = async () => {
    if (!notificationSettings.email_address) {
      toast.error("Please add your email address first");
      return;
    }
    
    setEmailSending(true);
    try {
      const res = await api.post("/notifications/test-email");
      toast.success(res.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test email");
    } finally {
      setEmailSending(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      event_type: "payment",
      date: new Date().toISOString().split('T')[0],
      amount: "",
      category: "",
      is_recurring: false,
      recurring_frequency: "monthly",
      notes: "",
      reminder_days: 3
    });
  };

  // Calendar grid calculation
  const calendarDays = useMemo(() => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    const lastDay = new Date(currentYear, currentMonth + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    
    const days = [];
    
    // Previous month days
    const prevMonth = new Date(currentYear, currentMonth, 0);
    for (let i = startingDay - 1; i >= 0; i--) {
      days.push({
        day: prevMonth.getDate() - i,
        currentMonth: false,
        date: null
      });
    }
    
    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, '0')}-${String(i).padStart(2, '0')}`;
      days.push({
        day: i,
        currentMonth: true,
        date: dateStr
      });
    }
    
    // Next month days
    const remaining = 42 - days.length;
    for (let i = 1; i <= remaining; i++) {
      days.push({
        day: i,
        currentMonth: false,
        date: null
      });
    }
    
    return days;
  }, [currentMonth, currentYear]);

  const getEventsForDate = (dateStr) => {
    if (!dateStr) return [];
    return events.filter(e => e.date === dateStr);
  };

  const today = new Date().toISOString().split('T')[0];

  const prevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const handleDateClick = (dateStr) => {
    if (!dateStr) return;
    setSelectedDate(dateStr);
    setFormData(prev => ({ ...prev, date: dateStr }));
  };

  // Calculate total payments this month
  const monthlyPayments = events
    .filter(e => e.event_type === "payment" && e.amount)
    .reduce((sum, e) => sum + e.amount, 0);

  return (
    <Layout title="Payment Calendar">
      <div className="space-y-6">
        {/* Header Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="total-events-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <CalendarIcon className="w-4 h-4 text-violet-500" />
                <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Events This Month</p>
              </div>
              <p className="text-3xl font-bold text-violet-500 font-mono">{events.length}</p>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="monthly-payments-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="w-4 h-4 text-red-500" />
                <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Monthly Payments</p>
              </div>
              <p className="text-3xl font-bold text-red-500 font-mono">{formatCurrency(monthlyPayments)}</p>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="upcoming-count-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Clock className="w-4 h-4 text-amber-500" />
                <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Upcoming (14 days)</p>
              </div>
              <p className="text-3xl font-bold text-amber-500 font-mono">{upcoming.upcoming_events.length}</p>
            </CardContent>
          </Card>
          
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="reminders-count-card">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-1">
                <Bell className="w-4 h-4 text-primary" />
                <p className="text-xs font-mono uppercase tracking-widest text-zinc-500">Active Reminders</p>
              </div>
              <p className="text-3xl font-bold text-primary font-mono">{upcoming.reminders.length}</p>
            </CardContent>
          </Card>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-3">
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="bg-primary text-black font-bold uppercase tracking-wide rounded-sm hover:bg-primary/90"
                data-testid="add-event-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                Add Event
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-white/10 rounded-none max-w-md">
              <DialogHeader>
                <DialogTitle className="text-white" style={{ fontFamily: 'Manrope' }}>
                  Add Calendar Event
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-zinc-300">Title</Label>
                  <Input
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    placeholder="e.g., Electric Bill"
                    required
                    data-testid="event-title-input"
                    className="bg-black/50 border-white/10 rounded-sm"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Type</Label>
                    <Select
                      value={formData.event_type}
                      onValueChange={(value) => setFormData({ ...formData, event_type: value })}
                    >
                      <SelectTrigger className="bg-black/50 border-white/10 rounded-sm" data-testid="event-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-white/10">
                        <SelectItem value="payment">Payment Due</SelectItem>
                        <SelectItem value="income">Income Expected</SelectItem>
                        <SelectItem value="reminder">Reminder</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Date</Label>
                    <Input
                      type="date"
                      value={formData.date}
                      onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                      required
                      data-testid="event-date-input"
                      className="bg-black/50 border-white/10 rounded-sm"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Amount (Optional)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.amount}
                      onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                      placeholder="0.00"
                      data-testid="event-amount-input"
                      className="bg-black/50 border-white/10 rounded-sm font-mono"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Category</Label>
                    <Select
                      value={formData.category}
                      onValueChange={(value) => setFormData({ ...formData, category: value })}
                    >
                      <SelectTrigger className="bg-black/50 border-white/10 rounded-sm" data-testid="event-category-select">
                        <SelectValue placeholder="Select..." />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-white/10">
                        <SelectItem value="electric">Electric</SelectItem>
                        <SelectItem value="water">Water</SelectItem>
                        <SelectItem value="cable">Cable/Internet</SelectItem>
                        <SelectItem value="insurance">Insurance</SelectItem>
                        <SelectItem value="car_payment">Car Payment</SelectItem>
                        <SelectItem value="subscription">Subscription</SelectItem>
                        <SelectItem value="rent">Rent/Mortgage</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-3 bg-white/5 border border-white/10">
                    <div className="flex items-center gap-2">
                      <Repeat className="w-4 h-4 text-zinc-400" />
                      <span className="text-sm text-zinc-300">Recurring Event</span>
                    </div>
                    <Switch
                      checked={formData.is_recurring}
                      onCheckedChange={(checked) => setFormData({ ...formData, is_recurring: checked })}
                      data-testid="recurring-toggle"
                    />
                  </div>
                  
                  {formData.is_recurring && (
                    <Select
                      value={formData.recurring_frequency}
                      onValueChange={(value) => setFormData({ ...formData, recurring_frequency: value })}
                    >
                      <SelectTrigger className="bg-black/50 border-white/10 rounded-sm" data-testid="frequency-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0A0A0A] border-white/10">
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="yearly">Yearly</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-300">Remind me (days before)</Label>
                  <Input
                    type="number"
                    min="0"
                    max="30"
                    value={formData.reminder_days}
                    onChange={(e) => setFormData({ ...formData, reminder_days: e.target.value })}
                    data-testid="reminder-days-input"
                    className="bg-black/50 border-white/10 rounded-sm font-mono w-24"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-300">Notes</Label>
                  <Input
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="Optional notes..."
                    data-testid="event-notes-input"
                    className="bg-black/50 border-white/10 rounded-sm"
                  />
                </div>
                
                <Button
                  type="submit"
                  className="w-full bg-primary text-black font-bold uppercase tracking-wide rounded-sm hover:bg-primary/90"
                  data-testid="submit-event-btn"
                >
                  Add Event
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          <Button 
            onClick={handleAiSuggest}
            disabled={aiLoading}
            className="bg-violet-500 text-white hover:bg-violet-600 rounded-sm"
            data-testid="ai-suggest-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            {aiLoading ? "Analyzing..." : "AI Auto-Suggest Payments"}
          </Button>

          <Button 
            onClick={handleSendReminders}
            disabled={emailSending}
            className="bg-blue-500 text-white hover:bg-blue-600 rounded-sm"
            data-testid="send-reminders-btn"
          >
            <Mail className="w-4 h-4 mr-2" />
            {emailSending ? "Sending..." : "Send Email Reminders"}
          </Button>

          <Dialog open={settingsDialogOpen} onOpenChange={setSettingsDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                variant="outline"
                className="border-white/20 text-zinc-300 hover:bg-white/10 rounded-sm"
                data-testid="notification-settings-btn"
              >
                <Settings className="w-4 h-4 mr-2" />
                Email Settings
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#0A0A0A] border-white/10 rounded-none max-w-md">
              <DialogHeader>
                <DialogTitle className="text-white flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Mail className="w-5 h-5 text-blue-500" />
                  Email Notification Settings
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-white/5 border border-white/10">
                  <div className="flex items-center gap-2">
                    <Bell className="w-4 h-4 text-zinc-400" />
                    <span className="text-sm text-zinc-300">Enable Email Reminders</span>
                  </div>
                  <Switch
                    checked={notificationSettings.email_enabled}
                    onCheckedChange={(checked) => setNotificationSettings({ ...notificationSettings, email_enabled: checked })}
                    data-testid="email-enabled-toggle"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-300">Email Address</Label>
                  <Input
                    type="email"
                    value={notificationSettings.email_address || ""}
                    onChange={(e) => setNotificationSettings({ ...notificationSettings, email_address: e.target.value })}
                    placeholder="your@email.com"
                    data-testid="notification-email-input"
                    className="bg-black/50 border-white/10 rounded-sm"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-zinc-300">Remind me (days before payment)</Label>
                  <Select
                    value={String(notificationSettings.reminder_days_before)}
                    onValueChange={(value) => setNotificationSettings({ ...notificationSettings, reminder_days_before: parseInt(value) })}
                  >
                    <SelectTrigger className="bg-black/50 border-white/10 rounded-sm" data-testid="reminder-days-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A0A] border-white/10">
                      <SelectItem value="1">1 day before</SelectItem>
                      <SelectItem value="2">2 days before</SelectItem>
                      <SelectItem value="3">3 days before</SelectItem>
                      <SelectItem value="5">5 days before</SelectItem>
                      <SelectItem value="7">7 days before</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    onClick={handleTestEmail}
                    disabled={emailSending || !notificationSettings.email_address}
                    variant="outline"
                    className="flex-1 border-white/20 text-zinc-300 hover:bg-white/10 rounded-sm"
                    data-testid="test-email-btn"
                  >
                    <Send className="w-4 h-4 mr-2" />
                    {emailSending ? "Sending..." : "Send Test Email"}
                  </Button>
                  <Button
                    onClick={handleSaveNotificationSettings}
                    className="flex-1 bg-primary text-black font-bold rounded-sm hover:bg-primary/90"
                    data-testid="save-settings-btn"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Save Settings
                  </Button>
                </div>
                
                {notificationSettings.email_enabled && notificationSettings.email_address && (
                  <div className="p-3 bg-primary/10 border border-primary/30 rounded-sm">
                    <div className="flex items-center gap-2">
                      <CheckCircle className="w-4 h-4 text-primary" />
                      <span className="text-sm text-primary">Email notifications enabled</span>
                    </div>
                    <p className="text-xs text-zinc-400 mt-1">
                      Reminders will be sent to {notificationSettings.email_address}
                    </p>
                  </div>
                )}
              </div>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Calendar Grid */}
          <Card className="bg-[#0A0A0A] border-white/10 rounded-none lg:col-span-2" data-testid="calendar-grid">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-lg text-white" style={{ fontFamily: 'Manrope' }}>
                {MONTHS[currentMonth]} {currentYear}
              </CardTitle>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={prevMonth}
                  className="text-zinc-400 hover:text-white"
                  data-testid="prev-month-btn"
                >
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={nextMonth}
                  className="text-zinc-400 hover:text-white"
                  data-testid="next-month-btn"
                >
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Day headers */}
              <div className="grid grid-cols-7 mb-2">
                {DAYS.map(day => (
                  <div key={day} className="text-center text-xs font-mono text-zinc-500 py-2">
                    {day}
                  </div>
                ))}
              </div>
              
              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-1">
                {calendarDays.map((dayObj, idx) => {
                  const dayEvents = getEventsForDate(dayObj.date);
                  const isToday = dayObj.date === today;
                  const isSelected = dayObj.date === selectedDate;
                  
                  return (
                    <div
                      key={idx}
                      onClick={() => handleDateClick(dayObj.date)}
                      className={`
                        min-h-[80px] p-1 border border-white/5 transition-all cursor-pointer
                        ${dayObj.currentMonth ? 'bg-white/5 hover:bg-white/10' : 'bg-transparent opacity-30'}
                        ${isToday ? 'border-primary' : ''}
                        ${isSelected ? 'border-violet-500 bg-violet-500/10' : ''}
                      `}
                      data-testid={`calendar-day-${dayObj.day}`}
                    >
                      <div className={`text-xs font-mono mb-1 ${isToday ? 'text-primary font-bold' : 'text-zinc-400'}`}>
                        {dayObj.day}
                      </div>
                      <div className="space-y-0.5">
                        {dayEvents.slice(0, 3).map((event) => (
                          <div
                            key={event.id}
                            className={`text-[10px] px-1 py-0.5 truncate rounded-sm ${EVENT_COLORS[event.event_type]} text-white`}
                            title={`${event.title}${event.amount ? ` - ${formatCurrency(event.amount)}` : ''}`}
                          >
                            {event.title}
                          </div>
                        ))}
                        {dayEvents.length > 3 && (
                          <div className="text-[10px] text-zinc-500 px-1">
                            +{dayEvents.length - 3} more
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Sidebar - Upcoming & Reminders */}
          <div className="space-y-6">
            {/* Reminders */}
            <Card className="bg-[#0A0A0A] border-amber-500/30 rounded-none" data-testid="reminders-card">
              <CardHeader>
                <CardTitle className="text-lg text-amber-400 flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
                  <Bell className="w-5 h-5" />
                  Reminders
                </CardTitle>
              </CardHeader>
              <CardContent>
                {upcoming.reminders.length === 0 ? (
                  <p className="text-sm text-zinc-500 text-center py-4">No upcoming reminders</p>
                ) : (
                  <div className="space-y-2">
                    {upcoming.reminders.slice(0, 5).map((reminder) => (
                      <div
                        key={reminder.id}
                        className="p-3 bg-amber-500/10 border border-amber-500/20"
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="text-sm font-medium text-white">{reminder.title}</p>
                            <p className="text-xs text-amber-400">
                              Due in {reminder.days_until} days
                            </p>
                          </div>
                          {reminder.amount && (
                            <span className="text-sm font-mono text-amber-400">
                              {formatCurrency(reminder.amount)}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Selected Date Events */}
            {selectedDate && (
              <Card className="bg-[#0A0A0A] border-violet-500/30 rounded-none" data-testid="selected-date-card">
                <CardHeader>
                  <CardTitle className="text-lg text-violet-400" style={{ fontFamily: 'Manrope' }}>
                    {new Date(selectedDate + 'T00:00:00').toLocaleDateString('en-US', { 
                      weekday: 'long', 
                      month: 'short', 
                      day: 'numeric' 
                    })}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {getEventsForDate(selectedDate).length === 0 ? (
                    <p className="text-sm text-zinc-500 text-center py-4">No events on this date</p>
                  ) : (
                    <div className="space-y-2">
                      {getEventsForDate(selectedDate).map((event) => (
                        <div
                          key={event.id}
                          className="p-3 border border-white/10 hover:border-white/20"
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2">
                              <div className={`w-2 h-2 rounded-full ${EVENT_COLORS[event.event_type]}`} />
                              <div>
                                <p className="text-sm font-medium text-white">{event.title}</p>
                                <p className="text-xs text-zinc-500 capitalize">{event.event_type}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-2">
                              {event.amount && (
                                <span className="text-sm font-mono text-zinc-300">
                                  {formatCurrency(event.amount)}
                                </span>
                              )}
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => handleDelete(event.id)}
                                className="h-7 w-7 text-zinc-500 hover:text-red-500"
                                data-testid={`delete-event-${event.id}`}
                              >
                                <Trash2 className="w-3 h-3" />
                              </Button>
                            </div>
                          </div>
                          {event.is_recurring && (
                            <div className="flex items-center gap-1 mt-2 text-xs text-zinc-500">
                              <Repeat className="w-3 h-3" />
                              <span className="capitalize">{event.recurring_frequency}</span>
                            </div>
                          )}
                          {event.ai_suggested && (
                            <div className="flex items-center gap-1 mt-1 text-xs text-violet-400">
                              <Sparkles className="w-3 h-3" />
                              <span>AI Suggested</span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Event List */}
            <Card className="bg-[#0A0A0A] border-white/10 rounded-none" data-testid="event-list-card">
              <CardHeader>
                <CardTitle className="text-lg text-white" style={{ fontFamily: 'Manrope' }}>
                  All Events This Month
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-sm text-zinc-500 text-center py-4">Loading...</p>
                ) : events.length === 0 ? (
                  <div className="text-center py-8">
                    <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-zinc-700" />
                    <p className="text-sm text-zinc-500">No events this month</p>
                    <p className="text-xs text-zinc-600 mt-1">Click "AI Auto-Suggest" to get started!</p>
                  </div>
                ) : (
                  <div className="space-y-2 max-h-80 overflow-y-auto">
                    {events.map((event) => (
                      <div
                        key={event.id}
                        className="flex items-center justify-between p-2 border border-white/5 hover:border-white/10"
                        data-testid={`event-item-${event.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${EVENT_COLORS[event.event_type]}`} />
                          <div>
                            <p className="text-sm text-white">{event.title}</p>
                            <p className="text-xs text-zinc-500">{event.date}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {event.amount && (
                            <span className="text-xs font-mono text-zinc-400">
                              {formatCurrency(event.amount)}
                            </span>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDelete(event.id)}
                            className="h-6 w-6 text-zinc-500 hover:text-red-500"
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
}
